'use client';

import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { avisoError } from '@/lib/avisoCentral';
import { loginSchema, type LoginValues } from '@/lib/login.schema';
import { iniciarSesion, type Sesion } from '@/lib/auth';

export type EstadoLogin = 'idle' | 'validando' | 'verificando' | 'ok' | 'error';

// Encapsula el formulario (RHF + Zod) y el flujo de ingreso: valida, llama al
// backend y expone un pequeño estado de progreso ('validando' → 'verificando'
// → 'ok') para que el botón dé retroalimentación clara mientras responde la API.
export function useLogin(onLogin: (s: Sesion) => void) {
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { cedula: '', clave: '' },
  });
  const [estado, setEstado] = useState<EstadoLogin>('idle');
  const [error, setError] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cualquier edición limpia el error y regresa el botón a su estado normal.
  useEffect(() => {
    const sub = form.watch(() => {
      setError('');
      setEstado((e) => (e === 'error' ? 'idle' : e));
    });
    return () => sub.unsubscribe();
  }, [form]);

  async function enviar(values: LoginValues) {
    setError('');
    setEstado('validando');
    // Si el backend tarda, el texto pasa a "Verificando acceso…" para no dejar el botón estático.
    timerRef.current = setTimeout(() => setEstado('verificando'), 550);
    try {
      const data = await iniciarSesion(values.cedula.trim(), values.clave);
      if (timerRef.current) clearTimeout(timerRef.current);
      setEstado('ok');
      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('usuario', JSON.stringify(data.usuario));
      // Login.tsx decide cuándo transicionar al shell (muestra antes el
      // aviso de acceso concedido al centro de la pantalla).
      onLogin(data.usuario);
    } catch (err) {
      if (timerRef.current) clearTimeout(timerRef.current);
      const msg = err instanceof Error ? err.message : 'No se pudo iniciar sesión.';
      setEstado('error');
      setError(msg);
      avisoError('No se pudo iniciar sesión', msg);
    }
  }

  function autocompletar(cedula: string, clave: string) {
    form.setValue('cedula', cedula, { shouldValidate: true });
    form.setValue('clave', clave, { shouldValidate: true });
    setError('');
    setEstado('idle');
  }

  return {
    form,
    estado,
    error,
    autocompletar,
    onSubmit: form.handleSubmit(enviar),
  };
}
